<template>
  <div class="pmf">
    <!-- Pay-in currency — only for a caller that asks it here (the concierge pay form). The top-up form keeps its
         currency inside the amount card, so it passes no suggestions and this card is not rendered there. -->
    <div v-if="currencySuggestions" :class="cls('card', 'mb')" :data-testid="testidPrefix + '-pay-currency'">
      <div :class="cls('cardLabel')">{{ t('vitrine.payMethod.currency') }}</div>
      <div v-if="kind === 'CRYPTO' && cryptoPair" :class="cls('pair')" :data-testid="testidPrefix + '-crypto-pair'">
        <span class="num">{{ cryptoPair.asset }} · {{ cryptoPair.networkLabel }}</span>
      </div>
      <CcyPicker
        v-else
        v-model="currency"
        :suggestions="currencySuggestions"
        :btn-testid="testidPrefix + '-pay-currency-btn'"
        :search-testid="testidPrefix + '-pay-currency-search'"
      />
    </div>

    <!-- bank: structured (name + country) -->
    <template v-if="kind === 'BANK'">
      <div :class="cls('label')">{{ t('vitrine.topup.bank') }}</div>
      <input
        v-model="bankName"
        :class="cls('input')"
        :maxlength="maxLen"
        :placeholder="t('vitrine.topup.bankNamePlaceholder')"
        :data-testid="testidPrefix + '-bank-name'"
      />
      <div :class="cls('label')">{{ t('vitrine.topup.bankCountry') }}</div>
      <SearchSelect
        v-model="bankCountry"
        :options="countryOptions"
        :aria-label="t('vitrine.topup.bankCountry')"
        :placeholder="t('vitrine.topup.country')"
        :input-testid="testidPrefix + '-bank-country'"
      />
      <!-- Sender type is not asked: the payer is always the client themselves (D-2). The caller sends the canonical
           constant; the concierge form also names whose account it is and, optionally, its last four digits. -->
      <template v-if="extended">
        <button
          type="button"
          :class="cls('check')"
          :data-testid="testidPrefix + '-account-own'"
          @click="accountOwnedByClient = accountOwnedByClient !== true"
        >
          <span :class="[...cls('checkbox'), accountOwnedByClient === true ? 'pmf__checkbox--on' : null]">
            <IconCheck v-if="accountOwnedByClient === true" :size="13" />
          </span>
          <span :class="cls('checkText')">{{ t('vitrine.payMethod.accountOwn') }}</span>
        </button>
        <div :class="cls('label')">{{ t('vitrine.payMethod.accountLast4') }}</div>
        <input
          :value="accountLast4"
          :class="cls('input')"
          inputmode="numeric"
          maxlength="4"
          :placeholder="t('vitrine.payMethod.accountLast4Placeholder')"
          :data-testid="testidPrefix + '-account-last4'"
          @input="onLast4"
        />
      </template>
      <div :class="cls('hint', 'mt')">{{ t(bankHintKey) }}</div>
    </template>

    <!-- cash: country + city (+ the time window on the concierge form) -->
    <template v-else-if="kind === 'CASH'">
      <div :class="cls('label')">{{ t('vitrine.topup.country') }}</div>
      <SearchSelect
        v-model="country"
        :options="countryOptions"
        :aria-label="t('vitrine.topup.country')"
        :placeholder="t('vitrine.topup.country')"
        :input-testid="testidPrefix + '-country'"
      />
      <div :class="cls('label')">{{ t('vitrine.topup.city') }}</div>
      <input
        v-model="city"
        :class="cls('input')"
        :maxlength="maxLen"
        :placeholder="t('vitrine.topup.cityPlaceholder')"
        :data-testid="testidPrefix + '-city'"
      />
      <template v-if="extended">
        <div :class="cls('label')">{{ t('vitrine.payMethod.window') }}</div>
        <div :class="cls('inline')">
          <input
            v-model="availableFrom"
            :class="cls('input')"
            :maxlength="maxLen"
            :placeholder="t('vitrine.payMethod.windowFrom')"
            :data-testid="testidPrefix + '-available-from'"
          />
          <input
            v-model="availableTo"
            :class="cls('input')"
            :maxlength="maxLen"
            :placeholder="t('vitrine.payMethod.windowTo')"
            :data-testid="testidPrefix + '-available-to'"
          />
        </div>
      </template>
      <div v-if="cashMinLabel" :class="cls('hint', 'min')" :data-testid="testidPrefix + '-cash-min'">
        {{ t('vitrine.topup.cashMinHint', { min: cashMinLabel }) }}
      </div>
      <div :class="cls('hint', 'mt')">{{ t(cashHintKey) }}</div>
    </template>

    <!-- comment (all) -->
    <div :class="cls('label')">{{ t('vitrine.topup.comment') }}</div>
    <input
      v-model="comment"
      :class="cls('input', 'soft')"
      :maxlength="maxLen"
      :placeholder="t('vitrine.topup.commentPlaceholder')"
      :data-testid="testidPrefix + '-comment'"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CcyPicker from '@/components/vitrine/CcyPicker.vue';
import SearchSelect from '@/components/vitrine/SearchSelect.vue';
import { IconCheck } from '@/components/ui/icons';
import type { SourceKind } from '@/api/topup';

/**
 * The pay-in method fields shared by the card top-up form and the concierge pay form (CONCIERGE_PAYMENTS_MODE.md
 * §11.1): bank — bank name and country (+ the account holder and its last four on the concierge form); cash — country
 * and city (+ the time window); a comment for every method; optionally the pay-in currency.
 *
 * The component asks, the caller decides: it holds no validation and builds no payload, so the top-up screen keeps
 * its own rules and wire body unchanged. `classPrefix` adds the caller's legacy class names next to the component's
 * own (`tu__input` stays a valid selector for the top-up E2E); the look comes from the component's styles.
 */
interface Option { value: string; label: string }

const props = withDefaults(defineProps<{
  kind: SourceKind;
  countryOptions: Option[];
  /** data-testid prefix: `<prefix>-bank-country`, `<prefix>-country`, … */
  testidPrefix?: string;
  /** Extra class prefix kept on every element (e.g. `tu` → `tu__input`). */
  classPrefix?: string;
  /** Concierge form: the account holder + last four (bank) and the time window (cash). */
  extended?: boolean;
  /** Renders the pay-in currency card; omitted on the top-up form. */
  currencySuggestions?: string[];
  /** CRYPTO on the concierge form is one fixed pair (USDT on TRON, D24) — shown instead of the currency picker. */
  cryptoPair?: { asset: string; networkLabel: string } | null;
  /** The cash minimum, already formatted with its currency; null — no hint. */
  cashMinLabel?: string | null;
  bankHintKey?: string;
  cashHintKey?: string;
}>(), {
  testidPrefix: 'topup',
  classPrefix: undefined,
  extended: false,
  currencySuggestions: undefined,
  cryptoPair: null,
  cashMinLabel: null,
  bankHintKey: 'vitrine.topup.bankHint',
  cashHintKey: 'vitrine.topup.cashHint',
});

const bankName = defineModel<string>('bankName', { default: '' });
const bankCountry = defineModel<string>('bankCountry', { default: '' });
/** null — left open (a manager's draft may not say); shown unticked, the client's tap answers it. */
const accountOwnedByClient = defineModel<boolean | null>('accountOwnedByClient', { default: true });
const accountLast4 = defineModel<string>('accountLast4', { default: '' });
const country = defineModel<string>('country', { default: '' });
const city = defineModel<string>('city', { default: '' });
const availableFrom = defineModel<string>('availableFrom', { default: '' });
const availableTo = defineModel<string>('availableTo', { default: '' });
const comment = defineModel<string>('comment', { default: '' });
const currency = defineModel<string>('currency', { default: '' });

const { t } = useI18n();

/**
 * The server's bound on a free-text concierge method field (PaymentDraftValidator FIELD_MAX). Only on the concierge
 * form: the top-up inputs never had a limit and keep none, so their behaviour is unchanged.
 */
const FIELD_MAX = 255;
const maxLen = computed(() => (props.extended ? FIELD_MAX : undefined));

function cls(name: string, modifier?: string): string[] {
  const out = [`pmf__${name}`];
  if (modifier) out.push(`pmf__${name}--${modifier}`);
  if (props.classPrefix) {
    out.push(`${props.classPrefix}__${name}`);
    if (modifier) out.push(`${props.classPrefix}__${name}--${modifier}`);
  }
  return out;
}

/** Digits only, at most four — the server accepts exactly four digits or nothing. */
function onLast4(e: Event) {
  const el = e.target as HTMLInputElement;
  const digits = el.value.replace(/\D/g, '').slice(0, 4);
  el.value = digits;
  accountLast4.value = digits;
}
</script>

<style scoped lang="scss">
/* The same metrics as the top-up form's own (.tu__*), so moving the fields here changed nothing on its screen. */
.pmf__card { border-radius: 18px; background: var(--bg-elev); border: 1px solid var(--hairline); padding: 16px; }
.pmf__card--mb { margin-bottom: 14px; }
.pmf__cardLabel { font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--text-3); font-weight: 600; }
.pmf__pair { margin-top: 8px; font-size: 17px; color: var(--gold-light); font-weight: 600; }
.pmf__label { font-size: 10.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--text-3); font-weight: 600; margin: 18px 4px 8px; }
.pmf__input {
  width: 100%; box-sizing: border-box; padding: 14px 16px; border-radius: 14px;
  background: var(--bg-elev); border: 1px solid var(--hairline-strong);
  color: var(--text); font-size: 13.5px; font-family: inherit; outline: none;
}
.pmf__input--soft { border-color: var(--hairline); }
.pmf__inline { display: flex; gap: 10px; align-items: center; }
.pmf__inline .pmf__input { flex: 1; min-width: 0; }
.pmf__hint { font-size: 11px; color: var(--text-3); line-height: 1.45; margin: 8px 4px 0; }
.pmf__hint--min { margin: 10px 4px 0; color: var(--text-2); }
.pmf__hint--mt { margin-top: 8px; }
.pmf__check { display: flex; align-items: center; gap: 10px; margin-top: 16px; background: none; border: none; padding: 0 4px; cursor: pointer; font-family: inherit; text-align: left; }
.pmf__checkbox {
  width: 21px; height: 21px; border-radius: 7px; flex-shrink: 0; box-sizing: border-box;
  border: 1.5px solid var(--hairline-strong); background: transparent;
  display: flex; align-items: center; justify-content: center; color: #1a1305;
}
.pmf__checkbox--on { background: var(--gold); border: none; }
.pmf__checkText { font-size: 13px; color: var(--text-2); }
</style>
